import { ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { addToCart } from "../../../utils/CartUtils";
import { useNotification } from "../../../context/NotificationContext";
import { formatPrice } from "../../../utils/productUtils";


export default function PopularItems({ items = [], isLoading }) {
  const { showNotification } = useNotification();

  const handleQuickAdd = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const result = addToCart(item, 1);

    showNotification({
      type: result.success ? "success" : "failed",
      title: result.success ? "Added to Cart" : "Action Failed",
      message: result.message
    });
  };

  return (
    <section
      className="py-20 md:py-28 px-6 bg-[#FAFAFA] dark:bg-[#1A1A1A]"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="mb-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2
                className="text-black dark:text-white font-medium font-serif leading-tight mb-3"
                style={{
                  fontSize: "clamp(32px, 5vw, 56px)",
                }}
              >
                Featured Rentals
              </h2>
              <p className="text-[#666666] dark:text-[#A0A0A0] text-lg md:text-xl max-w-2xl">
                Our most requested items for unforgettable events
              </p>
            </div>
            <Link
              to="/rentals/"
              className="hidden md:inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] font-semibold hover:gap-3 transition-all duration-200"
            >
              View All
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>

        {/* Loading Skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] h-[450px]"
              >
                <div className="h-64 bg-[#E0E0E0] dark:bg-[#2A2A2A]" />
                <div className="p-6">
                  <div className="h-3 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-1/4 mb-3" />
                  <div className="h-5 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-3/4 mb-3" />
                  <div className="h-3 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-1/2 mb-6" />
                  <div className="h-8 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && items.length === 0 && (
          <div className="text-center py-24 bg-white dark:bg-[#1E1E1E] rounded-3xl border border-[#E9E9E9] dark:border-[#333333] shadow-sm">
            <div className="mb-6 text-[#62397f]/30">
              <ShoppingCart size={80} className="mx-auto" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-black dark:text-white mb-3">
              No Featured Items
            </h3>
            <p className="text-[#666666] dark:text-[#A0A0A0] max-w-md mx-auto mb-8 text-lg">
              We're currently updating our featured collection. Please check our
              full catalog to see all available rentals.
            </p>
            <Link
              to="/rentals"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200"
            >
              Browse All Rentals
            </Link>
          </div>
        )}

        {/* Items Grid */}
        {!isLoading && items.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {items.map((item, index) => (
              <Link
                key={item.id || index}
                to={`/rental/${item.slug}`}
                className="block group bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] transition-all duration-300 hover:shadow-[0_12px_32px_rgba(139,92,246,0.12)] dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)] transform hover:-translate-y-2"
              >
                {/* Image Container */}
                <div className="relative h-64 overflow-hidden bg-[#F5F5F5] dark:bg-[#2A2A2A]">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Popular Badge */}
                  {item.popular && (
                    <div className="absolute top-4 left-4 px-3 py-1 bg-[#62397f] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                      Popular
                    </div>
                  )}
                  {/* Quick Add Button */}
                  <button
                    onClick={(e) => handleQuickAdd(e, item)}
                    className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-[#2A2A2A] rounded-full flex items-center justify-center shadow-lg transform translate-y-16 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#62397f] hover:text-white text-[#62397f] dark:text-[#A78BFA]"
                    aria-label="Add to cart"
                  >
                    <ShoppingCart size={20} />
                  </button>
                </div>
                {/* Content */}
                <div className="p-6">
                  <p className="text-xs font-semibold text-[#62397f] dark:text-[#A78BFA] uppercase tracking-wider mb-2">
                    {item.category}
                  </p>
                  <h3 className="font-serif text-sm font-bold text-black dark:text-white mb-2 group-hover:text-[#62397f] dark:group-hover:text-[#A78BFA] transition-colors duration-200">
                    {item.name}
                  </h3>
                  <p className="text-sm text-[#999999] dark:text-[#666666] mb-3">
                    {item.qtyAvailable} available
                  </p>
                  <div className="flex flex-col lg:flex-row lg:items-baseline gap-1">
                    <span className="text-[14px] md:text-[16px] lg:text-[24px] font-bold text-[#62397f] dark:text-[#A78BFA]">
                      ₦{formatPrice(item.price)}
                    </span>
                    <span className="text-xs md:text-sm lg:text-sm text-[#999999] dark:text-[#666666] lg:ml-1">
                      {item.unit}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Mobile View All */}
        <div className="mt-12 text-center md:hidden">
          <Link
            to="/rentals/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200"
          >
            View All Rental Items
          </Link>
        </div>
      </div>
    </section>
  );
}
